/**
 * Coordinates and external map links for a point on the map.
 *
 * Shared by the POI sheet on every map and the peak detail panel on the peak
 * pages. These started life in the peak-bagging helpers; they live here because
 * the POI sheet needs exactly the same answers, and two implementations of
 * "what is this place's Street View link" is how the two quietly drift apart.
 */

/** "56.79689, -5.00368" — the form the POI sheet shows, 5dp. */
export function formatCoords (lat: number | null | undefined, lng: number | null | undefined): string {
  if (typeof lat !== 'number' || typeof lng !== 'number') return ''
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
}

/**
 * Google Street View at the point.
 *
 * `map_action=pano` with a viewpoint drops the viewer at the nearest panorama
 * to that point, which is what makes this work for a place with no pano exactly
 * on it — Google walks to the closest one it has.
 */
export function streetViewUrl (lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat.toFixed(6)},${lng.toFixed(6)}`
}

/**
 * Driving directions in Google Maps.
 *
 * No origin: leaving it out lets Google use wherever the viewer actually is,
 * which is right on a phone and gives them the search box on a desktop.
 */
export function drivingDirectionsUrl (lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  return 'https://www.google.com/maps/dir/?api=1' +
    `&destination=${lat.toFixed(6)},${lng.toFixed(6)}&travelmode=driving`
}

/** Deep link into the main map at this point. */
export function mapLinkFor (lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  return `/map?lng=${lng.toFixed(5)}&lat=${lat.toFixed(5)}`
}
